import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  Mail, Trash2, Pencil, ArrowLeft, ExternalLink, User as UserIcon, Building2, Calendar, Tag,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_BADGE = {
  new:         'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  handled:     'bg-emerald-100 text-emerald-700',
  spam:        'bg-rose-100 text-rose-700',
};

const STATUS_LABEL = {
  new: 'New',
  in_progress: 'In progress',
  handled: 'Handled',
  spam: 'Spam',
};

const ROLE_LABEL = {
  investor: 'Investor',
  social_actor: 'Social actor',
  project_manager: 'Project manager',
  administrator: 'Administrator',
  researcher: 'Researcher',
  other: 'Other',
};

const ContactRequests = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState('all');
  const [editing, setEditing] = useState(null);  // { item, status, notes }

  const fetchAll = useCallback(async () => {
    try {
      const url = tab === 'all'
        ? `${BACKEND_URL}/api/contact`
        : `${BACKEND_URL}/api/contact?status=${tab}`;
      const r = await axios.get(url, { headers });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load contact requests');
    }
    // eslint-disable-next-line
  }, [tab]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateStatus = async () => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${editing.item.id}`, {
        status: editing.status,
        notes: editing.notes || '',
      }, { headers });
      toast.success('Updated');
      setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const deleteRow = async (item) => {
    if (!window.confirm(`Delete the contact request from ${item.name}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/contact/${item.id}`, { headers });
      toast.success('Deleted');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4 text-slate-600" data-testid="contact-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 text-emerald-700 mb-1"><Mail className="w-5 h-5" /><span className="text-xs font-semibold uppercase tracking-wider">Inbox</span></div>
            <h1 className="text-3xl font-bold text-slate-900">Contact requests</h1>
            <p className="text-slate-600 text-sm mt-1">Lead enquiries submitted via the public landing page.</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-4">
          <TabsList className="bg-white border">
            <TabsTrigger value="all"         data-testid="contact-tab-all">All ({items.length || 0})</TabsTrigger>
            <TabsTrigger value="new"         data-testid="contact-tab-new">New ({counts.new || 0})</TabsTrigger>
            <TabsTrigger value="in_progress" data-testid="contact-tab-in-progress">In progress ({counts.in_progress || 0})</TabsTrigger>
            <TabsTrigger value="handled"     data-testid="contact-tab-handled">Handled ({counts.handled || 0})</TabsTrigger>
            <TabsTrigger value="spam"        data-testid="contact-tab-spam">Spam ({counts.spam || 0})</TabsTrigger>
          </TabsList>
        </Tabs>

        {items.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-500">No contact requests in this view yet.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <Card key={it.id} className="hover:shadow-md transition" data-testid={`contact-row-${it.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge className={`${STATUS_BADGE[it.status] || ''} border-0 text-xs`}>{STATUS_LABEL[it.status] || it.status}</Badge>
                        <Badge variant="outline" className="text-xs"><Tag className="w-3 h-3 mr-1" />{ROLE_LABEL[it.role] || it.role}</Badge>
                        <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(it.created_at).toLocaleString('en-GB')}</span>
                      </div>
                      <h3 className="font-bold text-slate-900 inline-flex items-center gap-2"><UserIcon className="w-4 h-4 text-slate-500" />{it.name}</h3>
                      {it.organisation && <p className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" />{it.organisation}</p>}
                      <p className="text-xs text-emerald-700 mt-1">
                        <a href={`mailto:${it.email}`} className="hover:underline inline-flex items-center gap-1">{it.email} <ExternalLink className="w-3 h-3" /></a>
                      </p>
                      <p className="text-sm text-slate-700 mt-3 leading-relaxed whitespace-pre-wrap">{it.message}</p>
                      {it.notes && (
                        <div className="mt-3 p-2 rounded bg-slate-50 border border-slate-200 text-xs text-slate-600">
                          <span className="font-semibold text-slate-700">Internal notes: </span>{it.notes}
                        </div>
                      )}
                      {it.handled_by && (
                        <p className="text-xs text-slate-500 mt-2">Last touched by {it.handled_by} · {it.handled_at && new Date(it.handled_at).toLocaleDateString('en-GB')}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setEditing({ item: it, status: it.status, notes: it.notes || '' })} data-testid={`contact-edit-${it.id}`}>
                        <Pencil className="w-3 h-3 mr-1" />Edit
                      </Button>
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteRow(it)} data-testid={`contact-delete-${it.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit dialog */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Update contact request</DialogTitle>
              <DialogDescription>{editing?.item?.name} · {editing?.item?.email}</DialogDescription>
            </DialogHeader>
            {editing && (
              <div className="space-y-3 pt-2">
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {Object.keys(STATUS_LABEL).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setEditing({ ...editing, status: s })}
                        className={`px-3 py-2 rounded border text-sm ${editing.status === s ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                        data-testid={`status-pick-${s}`}
                      >
                        {STATUS_LABEL[s]}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Internal notes</label>
                  <Textarea rows={4} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Outcome, follow-up date, anything relevant…" data-testid="contact-edit-notes" />
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                  <Button onClick={updateStatus} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="contact-edit-save">Save</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ContactRequests;
